/**
 * What: "Store data" section of the Settings page — the synced shop settings, honest per-kind
 *   build progress while a sync is active, and the order-collection history heat grid. Ports the
 *   former Shopper Intelligence "Store Data tab" trio (StoreSettingsSummary, StoreBuildProgress,
 *   SyncHistoryHeatmap) to s-* App Home elements now that Shopper Intelligence is removed as a
 *   nav concept; the three source components still exist under store-intelligence/ pending
 *   deletion in a later cleanup pass.
 * Transport / data-flow: Pure presentation over the same store-sync contracts; no network. The
 *   heat-grid day derivation (buildSyncHistory) and the rotating progress captions
 *   (useRotatingProgressPhrase) are reused unchanged from store-intelligence/ — pure logic with no
 *   Polaris dependency.
 */
import { SettingsSyncHistory } from './SettingsSyncHistory'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { ShopSettings, StoreSyncBuildStage, StoreSyncStatus } from '@testfeed/shopify-contracts'
import { formatTimeZoneName } from '../../../utils/formatters'
import { useRotatingProgressPhrase } from '../../store-intelligence/useRotatingProgressPhrase'

const settingsCopy = shopifyComponentCopy.storeIntelligenceStoreSettings
const progressCopy = shopifyComponentCopy.storeIntelligenceStoreBuildProgress

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="small-200">
      <s-text color="subdued">{label}</s-text>
      <s-text fontWeight="medium">{value}</s-text>
    </s-stack>
  )
}

/** Synced shop settings (currency, name, timezone, domain), or an explicit not-synced state. */
function StoreSettingsSummary({ settings }: { settings: ShopSettings | null }) {
  const timeZoneName = settings ? formatTimeZoneName(settings.ianaTimezone) : null
  return (
    <s-box border="base" borderRadius="base" padding="base">
      <s-stack direction="block" gap="small-200">
        <s-text fontWeight="semibold">{settingsCopy.heading}</s-text>
        <s-text color="subdued">{settings ? settingsCopy.subtitle : settingsCopy.notSyncedBody}</s-text>
        {settings && (
          <s-stack direction="block" gap="small-200">
            <SettingRow label={settingsCopy.currencyLabel} value={settings.currencyCode} />
            <SettingRow label={settingsCopy.shopNameLabel} value={settings.shopName} />
            {timeZoneName && <SettingRow label={settingsCopy.timezoneLabel} value={timeZoneName} />}
            <SettingRow label={settingsCopy.domainLabel} value={settings.primaryDomainHost} />
          </s-stack>
        )}
      </s-stack>
    </s-box>
  )
}

const ROW_LABEL: Record<StoreSyncBuildStage['kind'], string> = {
  settings: progressCopy.rowSettings,
  products: progressCopy.rowProducts,
  customers: progressCopy.rowCustomers,
  orders: progressCopy.rowOrders,
  inventory: progressCopy.rowInventory,
  locations: progressCopy.rowLocations,
  discounts: progressCopy.rowDiscounts,
  price_rules: progressCopy.rowPriceRules,
  content: progressCopy.rowContent,
  fulfillments: progressCopy.rowFulfillments,
}

const STATE_LABEL: Record<StoreSyncBuildStage['state'], string> = {
  queued: progressCopy.stateQueued,
  exporting: progressCopy.stateExporting,
  downloading: progressCopy.stateDownloading,
  loading: progressCopy.stateLoading,
  completed: progressCopy.stateCompleted,
  failed: progressCopy.stateFailed,
  scope_denied: progressCopy.stateNoAccess,
}

function stageProgressPhrases(kind: StoreSyncBuildStage['kind']) {
  if (kind === 'settings') return progressCopy.progressPhrasesSettings
  if (kind === 'products') return progressCopy.progressPhrasesProducts
  if (kind === 'customers') return progressCopy.progressPhrasesCustomers
  if (kind === 'orders') return progressCopy.progressPhrasesOrders
  return progressCopy.progressPhrasesOther
}

const FINISHED_STATES: ReadonlySet<StoreSyncBuildStage['state']> = new Set(['completed', 'failed', 'scope_denied'])

function BuildStageRow({ stage }: { stage: StoreSyncBuildStage }) {
  const inProgress = !FINISHED_STATES.has(stage.state)
  const phrase = useRotatingProgressPhrase(stageProgressPhrases(stage.kind), inProgress)
  const showUsable = inProgress && stage.usable
  return (
    <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="small-200">
      <s-stack direction="inline" gap="small-100" alignItems="center">
        <s-text>{ROW_LABEL[stage.kind]}</s-text>
        {phrase && <s-text color="subdued">- {phrase}</s-text>}
      </s-stack>
      <s-stack direction="inline" gap="small-200" alignItems="center">
        {stage.objectCount !== null && <s-text color="subdued">{progressCopy.recordCount(stage.objectCount)}</s-text>}
        {showUsable && <s-text color="subdued">{progressCopy.stateUsable}</s-text>}
        <s-text fontWeight="medium">{STATE_LABEL[stage.state]}</s-text>
      </s-stack>
    </s-stack>
  )
}

/** Honest per-kind sync progress, shown only while a build is actively running. */
function BuildProgress({ stages }: { stages: StoreSyncBuildStage[] }) {
  return (
    <s-banner tone="info" heading={shopifyComponentCopy.settingsIndex.syncingNowTitle}>
      <s-stack direction="block" gap="small-200">
        {stages.map((stage) => (
          <BuildStageRow key={stage.kind} stage={stage} />
        ))}
      </s-stack>
    </s-banner>
  )
}

export function StoreDataSection({
  shopSettings,
  buildStages,
  isSyncing,
  syncStatus,
}: {
  shopSettings: ShopSettings | null
  buildStages: StoreSyncBuildStage[] | null
  isSyncing: boolean
  syncStatus: StoreSyncStatus | null
}) {
  const copy = shopifyComponentCopy.settingsIndex
  return (
    <s-section heading={copy.storeDataSectionTitle}>
      <s-text color="subdued">{copy.storeDataSectionDescription}</s-text>
      <s-stack direction="block" gap="base">
        {isSyncing && buildStages && <BuildProgress stages={buildStages} />}
        <StoreSettingsSummary settings={shopSettings} />
        {syncStatus?.coverage && <SettingsSyncHistory status={syncStatus} />}
      </s-stack>
    </s-section>
  )
}
